import { Flex, Group, Text } from '@mantine/core'
import { Duration, plotGraphData } from '@ospace/common'
import { LeadsStatsData, LineChart } from '@ospace/leads-dashboard'
import { MoreInfo } from '@ospace/shared'
import React from 'react'

import { BaseCard } from '../BaseCard'
import { CircularChart } from '../CircularRatioChart'
import { SideBySideFigures } from '../SideBySideFigures'
import { TopCard } from './TopCard'

const GraphHeader = ({
  left,
  right,
  title,
  tooltip,
  children,
}: {
  title: string
  tooltip: string
  left: {
    label: string
    value: string | number
  }
  right: {
    label: string
    value: string | number
  }
  children?: React.ReactNode
}) => {
  return (
    <>
      <Group>
        <Text tt='uppercase' c='dimmed' fw={600} fz='md'>
          {title}
        </Text>
        <MoreInfo info={tooltip} />
      </Group>
      <SideBySideFigures left={left} right={right} style={{ marginTop: '1.5rem' }} />
      {children}
    </>
  )
}

const DEFAULT_COLOR = '#4C6EF5'

const LEADS_TOOLTIP =
  'The chart displays companies converted to leads during the selected time period. The ratio is relationship between companies discovered in the selected time period and companies that have been converted to leads in the selected time period.'

const MEETINGS_TOOLTIP =
  'The chart displays leads converted to meetings during the selected time period. The ratio is relationship between leads created in the selected time period and meetings booked in the selected time period.'

const CONVERTED_TOOLTIP =
  'The doughnut shows the percentage of opportunities generated in the selected time period and meetings booked in the selected time period.'

type LeadsCardProps = {
  duration: Duration
  stats: LeadsStatsData
}

export const LeadsCard = ({ stats, duration }: LeadsCardProps) => {
  const leadsChartData = plotGraphData(stats.lead.chart || {}, duration)
  const leadsChart = {
    title: 'NEW LEADS',
    left: {
      label: 'Leads',
      value: stats.lead.count,
    },
    right: {
      label: 'Ratio',
      value: `${stats.discovery.count}:${stats.lead.count}`,
    },
    tooltip: LEADS_TOOLTIP,
    chart: LineChart,
    color: 'white',
    data: leadsChartData.chartData.data,
    chartLabels: leadsChartData.chartData.chartLabels,
  }

  const meetingBookedChartData = plotGraphData(stats.meetingBooked.chart || {}, duration)
  const meetingBookedChart = {
    title: 'MEETINGS BOOKED',
    left: {
      label: 'Meetings',
      value: stats.meetingBooked.count,
    },
    right: {
      label: 'Ratio',
      value: `${stats.lead.count}:${stats.meetingBooked.count}`,
    },
    tooltip: MEETINGS_TOOLTIP,
    data: meetingBookedChartData.chartData.data,
    chartLabels: meetingBookedChartData.chartData.chartLabels,
  }

  const convertedLeadsCount = stats.opportunity.count || 0
  const totalLeadsCount = stats.lead.count || 0
  const convertedLeadChart = {
    title: 'CONVERTED LEADS',
    left: {
      label: 'Total leads',
      value: totalLeadsCount,
    },
    right: {
      label: 'Converted leads',
      value: convertedLeadsCount,
    },
    tooltip: CONVERTED_TOOLTIP,
  }

  return (
    <>
      <TopCard {...leadsChart} />
      <BaseCard>
        <GraphHeader {...convertedLeadChart}>
          <CircularChart
            sections={[
              {
                value: convertedLeadsCount,
                color: DEFAULT_COLOR,
              },
            ]}
            total={`${Math.round((convertedLeadsCount / totalLeadsCount) * 100) || 0}%`}
          />
        </GraphHeader>
        <GraphHeader {...meetingBookedChart}>
          <LineChart
            data={[
              {
                id: 'pipeline',
                color: DEFAULT_COLOR,
                data: meetingBookedChart.data,
              },
            ]}
            enableArea={true}
          />
          <Group justify='space-between' fw={500} c='dimmed'>
            <Flex justify='flex-start'>{meetingBookedChart.chartLabels.leftLabel}</Flex>
            <Flex justify='flex-start'>{meetingBookedChart.chartLabels.rightLabel}</Flex>
          </Group>
        </GraphHeader>
      </BaseCard>
    </>
  )
}
